import { X, Menu, ArrowLeft, Plus, Maximize2, Minimize2 } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select"
import type { Agent } from "hooks/useAgents"

interface ChatHeaderProps {
  view: "new" | "thread" | "archive"
  onToggleArchive: () => void
  onClose: () => void
  onBackFromArchive: () => void
  onNewChat: () => void
  agents?: Agent[]
  selectedAgentId?: string | null
  onSelectAgent?: (agentId: string) => void
  isLoadingAgents?: boolean
  conversationTitle?: string | null
  showClose?: boolean
  isExpanded?: boolean
  onToggleExpanded?: () => void
}

export function ChatHeader({
  view,
  onToggleArchive,
  onClose,
  onBackFromArchive,
  onNewChat,
  agents = [],
  selectedAgentId,
  onSelectAgent,
  isLoadingAgents = false,
  conversationTitle,
  showClose = true,
  isExpanded = false,
  onToggleExpanded,
}: ChatHeaderProps) {
  const ExpandButton = () => onToggleExpanded ? (
    <button
      type="button"
      aria-label={isExpanded ? "Riduci chat" : "Espandi chat"}
      aria-pressed={isExpanded}
      onClick={onToggleExpanded}
      title={isExpanded ? "Riduci chat" : "Espandi chat"}
      className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
      style={{ color: "var(--hexa-text-secondary)" }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--hexa-surface-hover)" }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent" }}
    >
      {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
    </button>
  ) : null

  // Archive view: back button + title + new chat button
  if (view === "archive") {
    return (
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: "1px solid var(--hexa-border)" }}
      >
        <button
          aria-label="Torna alla chat"
          onClick={onBackFromArchive}
          className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
          style={{ color: "var(--hexa-text-secondary)" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--hexa-surface-hover)" }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent" }}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>

        <h2
          className="font-semibold text-sm"
          style={{ color: "var(--hexa-text-primary)" }}
        >
          Conversazioni
        </h2>

        <div className="flex items-center gap-1.5">
          <ExpandButton />
          <button
            aria-label="Nuova chat"
            onClick={onNewChat}
            className="flex items-center justify-center rounded-full transition-colors"
            style={{
              width: "28px",
              height: "28px",
              backgroundColor: "rgba(123,63,255,0.2)",
              color: "var(--hexa-text-primary)",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(123,63,255,0.3)" }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(123,63,255,0.2)" }}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          {showClose && (
            <button
              type="button"
              aria-label="Chiudi chat"
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--hexa-text-secondary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    )
  }

  // Default header for "new" and "thread" views
  return (
    <div
      className="flex items-center justify-between gap-2 px-2 sm:px-4 py-3 flex-shrink-0"
      style={{ borderBottom: "1px solid var(--hexa-border)" }}
    >
      <div className="flex min-w-0 items-center gap-2">
        {/* Archive Toggle */}
        <button
          aria-label="Archivio conversazioni"
          onClick={onToggleArchive}
          className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
          style={{ color: "var(--hexa-text-secondary)" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--hexa-surface-hover)" }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent" }}
        >
          <Menu className="w-4 h-4" />
        </button>

        {/* Agent Info */}
        <div className="flex min-w-0 items-center gap-2">
          <div className="w-7 h-7 shrink-0 rounded-full overflow-hidden ring-1 ring-[var(--hexa-border)]">
            <img
              src="/images/hexa-agent.png"
              alt="HEXA"
              width={28}
              height={28}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Conversation title when active, otherwise agent selector */}
          {conversationTitle ? (
            <h2 className="text-[var(--hexa-text-primary)] font-semibold text-sm truncate max-w-[180px]">
              {conversationTitle}
            </h2>
          ) : isLoadingAgents ? null : agents.length >= 2 && onSelectAgent ? (
            <Select
              value={selectedAgentId ?? undefined}
              onValueChange={onSelectAgent}
            >
              <SelectTrigger
                size="sm"
                className="border-none shadow-none bg-transparent text-[var(--hexa-text-primary)] font-semibold text-sm h-8 min-w-0 max-w-[160px] px-1"
              >
                <SelectValue placeholder="Agente" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.slug} value={agent.slug}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : agents.length === 1 ? (
            <span className="block truncate leading-8 px-1 text-[var(--hexa-text-primary)] font-semibold text-sm">
              {agents[0].name}
            </span>
          ) : null}
        </div>
      </div>

      {/* Right side actions */}
      <div className="flex shrink-0 items-center gap-1.5">
        <ExpandButton />
        {/* New chat */}
        <button
          aria-label="Nuova chat"
          onClick={onNewChat}
          title="Nuova chat"
          className="flex items-center justify-center rounded-full transition-colors"
          style={{
            width: "28px",
            height: "28px",
            backgroundColor: "rgba(123,63,255,0.2)",
            color: "var(--hexa-text-primary)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(123,63,255,0.3)" }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(123,63,255,0.2)" }}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>

        {/* Close */}
        {showClose && (
          <button
            aria-label="Chiudi chat"
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
            style={{ color: "var(--hexa-text-secondary)" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--hexa-surface-hover)" }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent" }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
